import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase, isConfigured } from '../lib/supabase.js'
import Seo from '../components/Seo.jsx'

const pctChange = (cur, base) => (base > 0 ? Math.round(((cur - base) / base) * 100) : cur > 0 ? 100 : 0)
const arrow = (n) => (n > 0 ? `▲ ${n}%` : n < 0 ? `▼ ${Math.abs(n)}%` : '—')
const cls = (n) => (n > 0 ? 'up' : n < 0 ? 'down' : 'flat')

export default function AdminStats() {
  const [stats, setStats] = useState(null)
  const [err, setErr] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isConfigured) { setLoading(false); return }
    supabase.rpc('admin_signup_stats').then(({ data, error }) => {
      if (error) setErr(error.message)
      else setStats(data)
      setLoading(false)
    })
  }, [])

  const vsLast = stats ? pctChange(stats.this_week, stats.last_week) : 0
  const vsAvg = stats ? pctChange(stats.this_week, stats.avg_week) : 0
  const weeks = stats?.by_week || []
  const maxWeek = Math.max(1, ...weeks.map((w) => w.count))
  const sources = stats?.by_source || []
  const maxSrc = Math.max(1, ...sources.map((s) => s.count))
  const campaigns = stats?.by_campaign || []
  const maxCmp = Math.max(1, ...campaigns.map((c) => c.count))
  const contents = stats?.by_content || []
  const maxCnt = Math.max(1, ...contents.map((c) => c.count))
  const device = stats?.by_device || {}
  const dTotal = Object.values(device).reduce((a, b) => a + b, 0) || 1
  const optInPct = stats?.total ? Math.round((stats.opt_in / stats.total) * 100) : 0

  return (
    <main className="page">
      <Seo title="Signup Stats | CalculateMyPeptides" noindex />
      <div className="container page-narrow">
        <Link to="/admin" className="pep-back">← Admin</Link>
        <span className="pill blue" style={{ marginBottom: 8 }}>Admin</span>
        <h1 className="section-title">Signup Stats</h1>
        <p className="section-sub" style={{ maxWidth: '60ch' }}>A quick glance at your own database — no Google Analytics needed.</p>

        {loading && <div className="card empty"><div className="spinner" /><p style={{ marginTop: 12 }}>Loading…</p></div>}

        {!loading && !isConfigured && (
          <div className="config-notice"><strong>Backend not connected.</strong></div>
        )}

        {!loading && err && (
          <div className="config-notice">
            <strong>Couldn’t load stats.</strong>
            <p style={{ marginBottom: 0 }}>{/not authorized/i.test(err) ? 'You must be signed in as an admin.' : <>Run <code>supabase/admin-stats.sql</code> in Supabase, then reload. ({err})</>}</p>
          </div>
        )}

        {!loading && stats && (
          <>
            <div className="stat-tiles">
              <div className="card stat-tile">
                <span className="stat-num">{stats.this_week}</span>
                <span className="stat-lbl">This week</span>
                <span className={`stat-delta ${cls(vsLast)}`}>{arrow(vsLast)} vs last week</span>
              </div>
              <div className="card stat-tile">
                <span className="stat-num">{stats.last_week}</span>
                <span className="stat-lbl">Last week</span>
                <span className={`stat-delta ${cls(vsAvg)}`}>{arrow(vsAvg)} vs 8-wk avg</span>
              </div>
              <div className="card stat-tile">
                <span className="stat-num">{stats.total}</span>
                <span className="stat-lbl">Total users</span>
                <span className="stat-delta flat">{stats.opt_in} opted in ({optInPct}%)</span>
              </div>
            </div>

            <section className="pep-section">
              <h2 className="pep-h2">Signups per week</h2>
              <div className="week-bars">
                {weeks.map((w) => (
                  <div className="week-bar" key={w.week} title={`${w.count} on week of ${w.week}`}>
                    <div className="week-bar-fill" style={{ height: `${Math.round((w.count / maxWeek) * 100)}%` }}><span>{w.count}</span></div>
                    <span className="week-bar-lbl">{w.week}</span>
                  </div>
                ))}
                {weeks.length === 0 && <p className="muted small">No signups in the last 8 weeks.</p>}
              </div>
            </section>

            <section className="pep-section">
              <h2 className="pep-h2">Where signups came from</h2>
              {sources.map((s) => (
                <div className="src-row" key={s.source}>
                  <span className="src-name">{s.source}</span>
                  <span className="src-bar"><span className="src-bar-fill" style={{ width: `${Math.round((s.count / maxSrc) * 100)}%` }} /></span>
                  <span className="src-count">{s.count}</span>
                </div>
              ))}
              <p className="muted small" style={{ marginTop: 8 }}>“direct” = untagged. Tag your links in the <Link to="/admin/utm">UTM builder</Link> so more of these resolve.</p>
            </section>

            <section className="pep-section">
              <h2 className="pep-h2">Signups by campaign</h2>
              {campaigns.map((c) => (
                <div className="src-row" key={c.campaign}>
                  <span className="src-name">{c.campaign}</span>
                  <span className="src-bar"><span className="src-bar-fill" style={{ width: `${Math.round((c.count / maxCmp) * 100)}%` }} /></span>
                  <span className="src-count">{c.count}</span>
                </div>
              ))}
              {campaigns.length === 0 && <p className="muted small">No campaign data yet.</p>}
              <p className="muted small" style={{ marginTop: 8 }}>Isolates one ad (e.g. <code>tiktok-signups</code>) from other traffic on the same source. “(none)” = untagged.</p>
            </section>

            {contents.length > 0 && (
              <section className="pep-section">
                <h2 className="pep-h2">By ad creative <span className="muted" style={{ fontWeight: 400, fontSize: '.85rem' }}>(utm_content)</span></h2>
                {contents.map((c) => (
                  <div className="src-row" key={c.content}>
                    <span className="src-name">{c.content}</span>
                    <span className="src-bar"><span className="src-bar-fill" style={{ width: `${Math.round((c.count / maxCnt) * 100)}%` }} /></span>
                    <span className="src-count">{c.count}</span>
                  </div>
                ))}
                <p className="muted small" style={{ marginTop: 8 }}>Which creative variant converted — tag each ad with a different <code>utm_content</code>.</p>
              </section>
            )}

            <section className="pep-section">
              <h2 className="pep-h2">Device</h2>
              <div className="src-row">
                <span className="src-name">📱 Mobile</span>
                <span className="src-bar"><span className="src-bar-fill" style={{ width: `${Math.round(((device.mobile || 0) / dTotal) * 100)}%` }} /></span>
                <span className="src-count">{device.mobile || 0} ({Math.round(((device.mobile || 0) / dTotal) * 100)}%)</span>
              </div>
              <div className="src-row">
                <span className="src-name">🖥️ Desktop</span>
                <span className="src-bar"><span className="src-bar-fill" style={{ width: `${Math.round(((device.desktop || 0) / dTotal) * 100)}%` }} /></span>
                <span className="src-count">{device.desktop || 0} ({Math.round(((device.desktop || 0) / dTotal) * 100)}%)</span>
              </div>
            </section>

            <p className="muted small" style={{ marginTop: 8 }}>
              Device &amp; source are captured at signup, so figures fill in going forward — older accounts show as “unknown/direct.”
            </p>
          </>
        )}
      </div>
    </main>
  )
}
